import { SeoOrFaviconTag } from 'react-datocms/seo';
import { TypedDocumentNode } from '@datocms/cda-client';
import { Block } from 'components/Blocks';
import gqlBase from 'graphql-tag';

const gql = <Result = unknown, Variables = unknown>(
  literals: string | readonly string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...args: any[]
): TypedDocumentNode<Result, Variables> => gqlBase(literals, ...args);

export default gql;

export interface Image {
  url: string;
  src?: string;
  alt?: string;
  width: number;
  height: number;
  bgColor?: string;
  _editingUrl?: string;
}

export const ImageFragment = gql<Image>`
  fragment Image on FileField {
    url
    alt
    width
    height
  }
`;

export interface ResponsiveImage {
  responsiveImage: {
    src: string;
    srcSet: string;
    width: number;
    height: number;
    alt: string;
    title: string;
    bgColor: string;
  };
}

export const ResponsiveImageFragment = gql<ResponsiveImage>`
  fragment ResponsiveImage on FileField {
    responsiveImage {
      #(imgixParams: { auto: format, w: 800 })
      # always required
      src
      srcSet
      width
      height

      # not required, but strongly suggested!
      alt
      title

      # blur-up placeholder, JPEG format, base64-encoded, or...
      base64
      # background color placeholder
      bgColor

      # you can omit sizes if you explicitly pass the sizes prop to the image component
      sizes
    }
  }
`;

export interface MetaTags {
  _seoMetaTags: SeoOrFaviconTag[];
}

export const MetaTagsFragment = gql<MetaTags>`
  fragment MetaTags on RecordInterface {
    _seoMetaTags {
      tag
      content
      attributes
    }
  }
`;

export const SimpleContentBlocksFragment = gql<Block[]>`
  fragment SimpleContentBlocks on RecordInterface {
    id
    __typename
    ... on PersonBlockRecord {
      title
      subtitle
      cta
      variant
      image {
        ...ResponsiveImage
      }
    }
    ... on VideoBlockRecord {
      image {
        ...Image
      }
      video {
        url
        title
        thumbnailUrl
      }
    }
    ... on ImageBlockRecord {
      image {
        ...ResponsiveImage
      }
      imageSubtitle
    }
    ... on FeaturesItemBlockRecord {
      title
      subtitle
    }
    ... on IconFeatureBlockRecord {
      title
      subtitle
      icon
      theme
      align
    }
    ... on CtaBlockRecord {
      content
      variant
      size
    }
    ... on TestimonialBlockRecord {
      quote
      person {
        image {
          ...ResponsiveImage
        }
        title
        subtitle
      }
    }
    ... on TimelineBlockRecord {
      items {
        title
        subtitle
        icon
      }
    }
    ... on InfluencerBlockRecord {
      quote
      cta
      image {
        ...ResponsiveImage
      }
      person {
        image {
          ...ResponsiveImage
        }
        title
        subtitle
      }
    }
  }
`;

export const StructuredContentFragment = gql<Block[]>`
  ${SimpleContentBlocksFragment}
  fragment StructuredContent on RecordInterface {
    id
    __typename
    ...SimpleContentBlocks
    ... on AccordionEntryRecord {
      title
      content {
        value
        blocks
        inlineBlocks
        links
      }
      category
    }
    ... on CtaCardBlockRecord {
      content {
        id
        description: subtitle
        title
        icon
        cta
      }
    }
    ... on FeatureBlockRecord {
      title
      subtitle
      cta
      icon
    }
    ... on SubscriptionBlockRecord {
      ctaTitle
      webhookUrl
      successLabel
      inputLabel
      errorLabel
      invalidEmailLabel
    }
    ... on CarouselBlockRecord {
      infiniteLoop
      carouselImages {
        ...ResponsiveImage
      }
    }
    ... on StatBlockRecord {
      title
      subtitle
      description
      backgroundColor
      blockSize
    }
    ... on TrustpilotBlockRecord {
      businessUnitId
      templateId
      theme
    }
    ... on ValuesCloudBlockRecord {
      image {
        ...ResponsiveImage
      }
      text1
      text2
      text3
      text4
    }
    ... on EventCardBlockRecord {
      image {
        ...ResponsiveImage
      }
      title
      description
      cta
      location
      startDate
      endDate
    }
    ... on RichTextBlockRecord {
      align
      content {
        value
        links {
          __typename
          id
          slug
        }
        blocks {
          ...SimpleContentBlocks
        }
        inlineBlocks {
          ...SimpleContentBlocks
        }
      }
    }
    ... on SchedulingBlockRecord {
      calendarUrl
    }
    ... on GridBlockRecord {
      rows
      columns
      cells {
        ...SimpleContentBlocks
        ... on RichTextBlockRecord {
          align
          content {
            value
            blocks {
              ...SimpleContentBlocks
            }
            inlineBlocks {
              ...SimpleContentBlocks
            }
          }
        }
      }
    }
    ... on ContentBlockRecord {
      variant
      columnWidths
      column1 {
        ...SimpleContentBlocks
        ... on RichTextBlockRecord {
          align
          content {
            value
            blocks {
              ...SimpleContentBlocks
            }
            inlineBlocks {
              ...SimpleContentBlocks
            }
          }
        }
      }
      column2 {
        ...SimpleContentBlocks
        ... on RichTextBlockRecord {
          align
          content {
            value
            blocks {
              ...SimpleContentBlocks
            }
            inlineBlocks {
              ...SimpleContentBlocks
            }
          }
        }
      }
      column3 {
        ...SimpleContentBlocks
        ... on RichTextBlockRecord {
          align
          content {
            value
            blocks {
              ...SimpleContentBlocks
            }
            inlineBlocks {
              ...SimpleContentBlocks
            }
          }
        }
      }
    }
    ... on ComparisonBlockRecord {
      ourLogo {
        ...Image
      }
      ourLabel
      competitorsLabel
      cta
      ctaLabel
      items {
        id
        category
        ourOfferTitle
        ourOfferDescription
        competitorOfferTitle
        competitorOfferDescription
      }
    }
  }
`;
